
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { ValidationContent } from '@/components/validator/ValidationContent';

const Validator = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [pixelId, setPixelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [checkingWebsite, setCheckingWebsite] = useState(false);
  const [websiteCheckResult, setWebsiteCheckResult] = useState<any>(null);
  
  useEffect(() => {
    document.title = "Pixel Validator | Lead Extract";
    
    const pid = searchParams.get('pixel_id');
    setPixelId(pid);
    
    if (pid) {
      validatePixelInstallation(pid);
    } else {
      setError("Missing Pixel ID in URL parameters");
      setIsLoading(false);
    }
  }, [searchParams]);

  const validatePixelInstallation = async (pid: string) => {
    setIsLoading(true);
    setError(null);
    setValidationResult(null);
    
    try {
      console.log(`Validating pixel installation for ID: ${pid}`);
      
      const { data, error: testError } = await supabase.functions.invoke('test-visitor-email', {
        body: { 
          pixelId: pid,
          validateOnly: true 
        }
      });
      
      if (testError) {
        console.error("Error calling test-visitor-email function:", testError);
        setError(`Validation failed: ${testError.message}`);
        setIsLoading(false);
        return;
      }
      
      console.log("Validation result:", data);
      setValidationResult(data);
      
      // Check if we already have website check results
      if (data?.details?.websiteCheckResult) {
        setWebsiteCheckResult(data.details.websiteCheckResult);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error("Error validating pixel:", err);
      setError(`Validation failed: ${err instanceof Error ? err.message : String(err)}`);
      setIsLoading(false);
    }
  };

  const handleTestEmailNotification = async () => {
    if (!pixelId) return;
    
    setIsValidating(true);
    
    try {
      const { data, error: testError } = await supabase.functions.invoke('test-visitor-email', {
        body: { pixelId }
      });
      
      if (testError) {
        console.error("Error testing email notification:", testError);
        setError(`Test email failed: ${testError.message}`);
        setIsValidating(false);
        return;
      }
      
      console.log("Test email result:", data);
      setValidationResult({
        ...validationResult,
        emailTestResult: data
      });
      setIsValidating(false);
    } catch (err) {
      console.error("Error testing email notification:", err);
      setError(`Test email failed: ${err instanceof Error ? err.message : String(err)}`);
      setIsValidating(false);
    }
  };

  const handleCheckWebsite = async () => {
    if (!pixelId) return;
    
    setCheckingWebsite(true);
    
    try {
      const { data, error: checkError } = await supabase.functions.invoke('test-visitor-email', {
        body: { 
          pixelId,
          validateOnly: true,
          checkWebsite: true
        }
      });
      
      if (checkError) {
        console.error("Error checking website:", checkError);
        setError(`Website check failed: ${checkError.message}`);
        setCheckingWebsite(false);
        return;
      }
      
      console.log("Website check result:", data);
      
      if (data?.details?.websiteCheckResult) {
        setWebsiteCheckResult(data.details.websiteCheckResult);
      }
      
      setCheckingWebsite(false);
    } catch (err) {
      console.error("Error checking website:", err);
      setError(`Website check failed: ${err instanceof Error ? err.message : String(err)}`);
      setCheckingWebsite(false);
    }
  };

  const getValidatorContent = () => {
    if (error) {
      return (
        <div className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      );
    }

    if (!validationResult) {
      return (
        <div className="flex items-center justify-center h-full flex-col p-6 text-center">
          <h3 className="text-lg font-medium mb-2">No Validation Results</h3>
          <p className="text-muted-foreground">
            No validation results available yet.
          </p>
        </div>
      );
    }

    if (validationResult.success) {
      return (
        <ValidationContent 
          validationResult={validationResult}
          websiteCheckResult={websiteCheckResult}
          checkingWebsite={checkingWebsite}
          isValidating={isValidating}
          onCheckWebsite={handleCheckWebsite}
          onTestEmailNotification={handleTestEmailNotification}
        />
      );
    }

    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Validation failed: {validationResult.error || "Unknown error"}</AlertDescription>
        </Alert>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Pixel Installation Validator</CardTitle>
          <CardDescription>
            This tool checks if your tracking pixel is correctly installed and collecting data on your website.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 min-h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-[500px]">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
                <p className="text-muted-foreground">Validating pixel installation...</p>
              </div>
            </div>
          ) : (
            getValidatorContent()
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Validator;
